'use client'

import { useState } from 'react'

/**
 * ImpersonationBanner — shows a fixed banner when an admin is "logged in as"
 * another user. Impersonation genuinely swaps the browser session to the
 * target, so this banner is the way back: Exit restores the admin's session.
 *
 * The impersonation check happens SERVER-SIDE in the mounting layout
 * (`getImpersonationLabel` reads the httpOnly marker cookie during render —
 * the layouts are already dynamic), so this component is purely
 * presentational. The old client-side fetch of
 * /api/admin/impersonate/status ran once per page view for every visitor
 * site-wide — the most-invoked admin API path in production for a feature
 * only staff ever use.
 */
export function ImpersonationBanner({
  impersonatedLabel = null,
}: {
  /** Impersonated user's email/id, or null when not impersonating. */
  impersonatedLabel?: string | null
}) {
  const impersonating = impersonatedLabel
  const [exiting, setExiting] = useState(false)

  const handleExit = async () => {
    setExiting(true)
    try {
      await fetch('/api/admin/impersonate', { method: 'DELETE' })
      // Exit restored the admin's session server-side — a full reload makes
      // every server component re-render as the admin again.
      // Deliberate hard reload: every server component must re-render under the restored session.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = '/admin/users'
    } catch {
      setExiting(false)
    }
  }

  if (!impersonating) return null

  return (
    <div
      className="sticky top-0 left-0 right-0 z-[9999] bg-yellow-400 text-black text-center py-2 px-4 text-sm font-bold flex items-center justify-center gap-4"
      role="alert"
    >
      <span>You are viewing the site as {impersonating}</span>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="bg-black text-yellow-400 px-3 py-1 text-xs font-bold hover:bg-gray-800 disabled:opacity-50"
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  )
}
